import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';

import {
  applySyntheticBondReferenceCurvePreset,
  clearSyntheticBondReferenceCurvePreset,
  createSyntheticBondReferenceCurvePreset,
  fetchSyntheticBondReferenceCurvePresets,
  SYNTHETIC_BOND_CURVE_TENOR_MONTHS,
  SyntheticBondGenerationDraft,
  SyntheticBondReferenceCurvePreset,
  syntheticBondReferenceCurvePresetKey,
  syntheticBondSourceMapsEqual,
  updateSyntheticBondReferenceCurvePreset,
} from 'state';
import { StoreState } from '../../../state/storeState';
import { InfoLabel } from '../BondsPlayground/ModelGuide';

type Props = {
  disabled?: boolean;
  draft: SyntheticBondGenerationDraft;
};

type CreateFamilyValues = {
  code: string;
  label: string;
};

const sourceMapIsComplete = (
  sources: SyntheticBondGenerationDraft['source_asset_ids_by_tenor_months'],
) => {
  const ids = SYNTHETIC_BOND_CURVE_TENOR_MONTHS.map(
    tenor => sources[String(tenor)],
  );
  return (
    ids.every((id): id is number => id !== null) && new Set(ids).size === 7
  );
};

const sourceMapIsEmpty = (
  sources: SyntheticBondGenerationDraft['source_asset_ids_by_tenor_months'],
) =>
  SYNTHETIC_BOND_CURVE_TENOR_MONTHS.every(
    tenor => sources[String(tenor)] === null,
  );

const ReferenceCurveFamilyField: React.FC<Props> = ({ disabled, draft }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm<CreateFamilyValues>();
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitted, setCreateSubmitted] = useState(false);
  const state = useSelector((store: StoreState) => store.app.synthetic_bonds);
  const currencyKey = draft.currency_id
    ? syntheticBondReferenceCurvePresetKey(draft.currency_id)
    : '';
  const entry = currencyKey
    ? state.referenceCurvePresetsByCurrencyKey[currencyKey]
    : undefined;
  const presets = useMemo(
    () => entry?.data?.results || [],
    [entry?.data?.results],
  );
  const selectedPreset = presets.find(
    preset => preset.id === state.selectedReferenceCurvePresetId,
  );
  const isModified = Boolean(
    selectedPreset &&
      !syntheticBondSourceMapsEqual(
        draft.source_asset_ids_by_tenor_months,
        selectedPreset.source_asset_ids_by_tenor_months,
      ),
  );
  const canSave = Boolean(
    draft.currency_id &&
      sourceMapIsComplete(draft.source_asset_ids_by_tenor_months),
  );

  useEffect(() => {
    if (!draft.currency_id) return;
    if (!entry?.fetching && !entry?.fetched && !entry?.error) {
      dispatch(
        fetchSyntheticBondReferenceCurvePresets({
          currency_id: draft.currency_id,
        }),
      );
    }
  }, [
    dispatch,
    draft.currency_id,
    entry?.error,
    entry?.fetched,
    entry?.fetching,
  ]);

  useEffect(() => {
    if (
      presets.length !== 1 ||
      state.selectedReferenceCurvePresetId ||
      !sourceMapIsEmpty(draft.source_asset_ids_by_tenor_months)
    ) {
      return;
    }
    const preset = presets[0];
    dispatch(
      applySyntheticBondReferenceCurvePreset({
        preset_id: preset.id,
        source_asset_ids_by_tenor_months: {
          ...preset.source_asset_ids_by_tenor_months,
        },
      }),
    );
  }, [
    dispatch,
    draft.source_asset_ids_by_tenor_months,
    presets,
    state.selectedReferenceCurvePresetId,
  ]);

  useEffect(() => {
    if (!createSubmitted || state.referenceCurvePresetSaving) return;
    if (!state.referenceCurvePresetSaveError) {
      setCreateOpen(false);
      form.resetFields();
    }
    setCreateSubmitted(false);
  }, [
    createSubmitted,
    form,
    state.referenceCurvePresetSaveError,
    state.referenceCurvePresetSaving,
  ]);

  const applyPreset = (preset: SyntheticBondReferenceCurvePreset) =>
    dispatch(
      applySyntheticBondReferenceCurvePreset({
        preset_id: preset.id,
        source_asset_ids_by_tenor_months: {
          ...preset.source_asset_ids_by_tenor_months,
        },
      }),
    );

  const selectPreset = (presetId?: string) => {
    if (!presetId) {
      dispatch(clearSyntheticBondReferenceCurvePreset({}));
      return;
    }
    const preset = presets.find(candidate => candidate.id === presetId);
    if (!preset) return;
    const wouldReplaceSources =
      !sourceMapIsEmpty(draft.source_asset_ids_by_tenor_months) &&
      !syntheticBondSourceMapsEqual(
        draft.source_asset_ids_by_tenor_months,
        preset.source_asset_ids_by_tenor_months,
      );
    if (!wouldReplaceSources) {
      applyPreset(preset);
      return;
    }
    Modal.confirm({
      title: `Apply ${preset.code} reference family?`,
      content:
        'This replaces all seven current source assignments. You can still change any tenor afterward.',
      okText: 'Replace sources',
      onOk: () => applyPreset(preset),
    });
  };

  const createPreset = () =>
    form.validateFields().then(values => {
      if (!draft.currency_id) return;
      setCreateSubmitted(true);
      dispatch(
        createSyntheticBondReferenceCurvePreset({
          currency_id: draft.currency_id,
          code: values.code,
          label: values.label,
          source_asset_ids_by_tenor_months: {
            ...draft.source_asset_ids_by_tenor_months,
          },
          is_active: true,
        }),
      );
    });

  const updatePreset = () => {
    if (!selectedPreset || !draft.currency_id) return;
    dispatch(
      updateSyntheticBondReferenceCurvePreset({
        id: selectedPreset.id,
        currency_id: draft.currency_id,
        code: selectedPreset.code,
        label: selectedPreset.label,
        source_asset_ids_by_tenor_months: {
          ...draft.source_asset_ids_by_tenor_months,
        },
        is_active: true,
      }),
    );
  };

  return (
    <div className="synthetic-bonds__field">
      <Typography.Text strong>
        <InfoLabel label="Reference curve family" title="Saved source defaults">
          Select a named same-currency curve such as DE or FR to populate all
          seven source tenors. Per-tenor source dropdowns remain editable.
        </InfoLabel>
      </Typography.Text>
      <Select<string>
        allowClear
        disabled={disabled || !draft.currency_id}
        loading={entry?.fetching}
        notFoundContent={
          entry?.error
            ? 'Reference families could not be loaded'
            : 'No saved reference families for this currency'
        }
        optionFilterProp="label"
        options={presets.map(preset => ({
          value: preset.id,
          label: `${preset.code} · ${preset.label}`,
        }))}
        placeholder={
          draft.currency_id
            ? 'Select reference family'
            : 'Select currency first'
        }
        showSearch
        value={state.selectedReferenceCurvePresetId}
        onChange={value => selectPreset(value)}
      />
      <Space size={[6, 6]} wrap>
        {selectedPreset && (
          <Tag color={isModified ? 'warning' : 'success'}>
            {selectedPreset.code} {isModified ? 'modified' : 'defaults applied'}
          </Tag>
        )}
        {selectedPreset && isModified && (
          <Button
            disabled={disabled}
            size="small"
            onClick={() => applyPreset(selectedPreset)}
          >
            Reset defaults
          </Button>
        )}
        <Button
          disabled={disabled || !canSave}
          size="small"
          onClick={() => {
            form.resetFields();
            setCreateOpen(true);
          }}
        >
          Save current curve
        </Button>
        {selectedPreset && isModified && canSave && (
          <Popconfirm
            description="Future uses of this family will receive the current seven assignments. Existing reviewed batches are unchanged."
            okText="Update defaults"
            title={`Update ${selectedPreset.code} defaults?`}
            onConfirm={updatePreset}
          >
            <Button disabled={disabled} size="small">
              Update defaults
            </Button>
          </Popconfirm>
        )}
        {entry?.error && (
          <Button
            size="small"
            onClick={() =>
              draft.currency_id &&
              dispatch(
                fetchSyntheticBondReferenceCurvePresets({
                  currency_id: draft.currency_id,
                }),
              )
            }
          >
            Retry
          </Button>
        )}
      </Space>
      {state.referenceCurvePresetSaveError && (
        <Alert
          message={state.referenceCurvePresetSaveError}
          showIcon
          type="error"
        />
      )}

      <Modal
        confirmLoading={state.referenceCurvePresetSaving}
        okText="Save family"
        open={createOpen}
        title="Save current sources as a reference family"
        onCancel={() => {
          if (!state.referenceCurvePresetSaving) setCreateOpen(false);
        }}
        onOk={createPreset}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            extra="Examples: US, DE, FR, IT-BTP"
            label="Family code"
            name="code"
            rules={[
              { required: true, message: 'Enter a family code.' },
              {
                pattern: /^[A-Za-z0-9][A-Za-z0-9_-]{0,29}$/,
                message:
                  'Use up to 30 letters, numbers, dashes, or underscores.',
              },
            ]}
          >
            <Input maxLength={30} placeholder="DE" />
          </Form.Item>
          <Form.Item
            label="Display label"
            name="label"
            rules={[{ required: true, message: 'Enter a display label.' }]}
          >
            <Input maxLength={150} placeholder="German sovereign curve" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReferenceCurveFamilyField;
